import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import useMembers from '@/hooks/useMembers';
import { useRoles } from '@/hooks/useRoles';
import { cn } from '@/lib/utils';
import { selectAgentById } from '@/redux/presence/selectors';
import { Presence, PresenceStatus } from '@/shared/types/presence';
import { RootState } from '@/shared/types/state';
import { timeFromNow } from '@/util/dateUtils';
import { getInitials, getRoleName } from '@/util/memberUtils';

const ACTIVE_NOW_THRESHOLD_SECONDS = 60;

const formatActivityLine = (t: TFunction, agent: Presence | null | undefined) => {
  if (!agent) {
    return null;
  }
  if (agent.status === PresenceStatus.OFFLINE) {
    return null;
  }
  if (agent.status === PresenceStatus.AWAY) {
    return t('avatar_tooltip.away');
  }
  if (!agent.last_activity_at) {
    return t('avatar_tooltip.active_now');
  }

  const ageMs = Date.now() - Date.parse(agent.last_activity_at);
  if (ageMs < ACTIVE_NOW_THRESHOLD_SECONDS * 1000) {
    return t('avatar_tooltip.active_now');
  }
  return t('avatar_tooltip.active_ago', { 0: timeFromNow(agent.last_activity_at) });
};

const statusDotClass = (status: PresenceStatus | undefined) => {
  if (status === PresenceStatus.ONLINE) {
    return 'bg-successful';
  }
  if (status === PresenceStatus.AWAY) {
    return 'bg-warning';
  }
  return null;
};

const UserInfoTooltipSkeleton = () => (
  <div className="flex items-start gap-3 px-3 py-2">
    <Skeleton className="size-8 rounded-full" />
    <div className="flex flex-col gap-1.5">
      <Skeleton className="h-3 w-32" />
      <Skeleton className="h-3 w-40" />
      <Skeleton className="h-3 w-20" />
    </div>
  </div>
);

type Props = { userId: string };

export const UserInfoTooltipContent = ({ userId }: Props) => {
  const { t } = useTranslation();
  const { getMember, listStatus: membersStatus } = useMembers();
  const { roles } = useRoles();
  const agent = useSelector((state: RootState) => selectAgentById(state, userId));

  if (membersStatus === 'pending') {
    return <UserInfoTooltipSkeleton />;
  }

  const member = getMember(userId);
  if (!member) {
    return null;
  }

  const roleLabel = getRoleName(member, roles);
  const activityLine = formatActivityLine(t, agent);
  const dotClass = statusDotClass(agent?.status);

  return (
    <div className="flex max-w-80 items-start gap-3 px-3 py-2">
      <Avatar size="sm">
        {member.avatar && <AvatarImage src={member.avatar} alt={member.name} loading="lazy" />}
        <AvatarFallback size="sm">{getInitials(member.name)}</AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-sm font-semibold">{member.name}</span>
        {member.email && (
          <span className="truncate text-xs text-text-primary-foreground/70">{member.email}</span>
        )}
        {roleLabel && roleLabel !== '-' && (
          <span className="truncate text-xs text-text-primary-foreground/70">{roleLabel}</span>
        )}
        {activityLine && (
          <span className="mt-1 flex items-center gap-1.5 text-xs text-text-primary-foreground/60">
            {dotClass && <span className={cn('size-1.5 rounded-full', dotClass)} />}
            <span className="truncate">{activityLine}</span>
          </span>
        )}
      </div>
    </div>
  );
};
